import React from "react";
import {
  Facebook as FacebookIcon,
  Instagram as InstagramLogo,
  Youtube as YoutubeLogo,
  LucideIcon, // Type for all Lucide icons
} from "lucide-react";

// 1. Define the props for the individual icon components
// This interface MUST extend React.HTMLAttributes<HTMLAnchorElement>
// or React.SVGProps<SVGSVGElement> to accept className.
// Since you want to pass the className to the anchor tag, we use HTMLAttributes.

interface SocialIconProps extends React.HTMLAttributes<HTMLAnchorElement> {
  // We explicitly keep `href` to make it clear this is a link component
  href: string;
  // We explicitly keep `Icon` to identify the Lucide Icon being used
  Icon: LucideIcon;
}

// 2. Create a single, highly flexible base component
// This component accepts all standard <a> tag attributes, including className.
const SocialIconBase: React.FC<SocialIconProps> = ({
  href,
  Icon,
  // Destructure and spread the rest of the props, including className
  ...props
}) => (
  // The 'props' object contains 'className', 'aria-label', etc.
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    // Apply all passed props, including className, to the <a> tag
    {...props}
    // You can keep base styles here, but the contact page overrides these.
    // className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
  >
    {/* The icon receives default size/color, but can be overridden by className/props */}
    <Icon className="h-5 w-5" />
  </a>
);

// 3. Export the platform-specific components using the base
// We use a functional component structure so it can accept props like className.

// Interface for simple icon usage (since the base component handles the link logic)
interface SimpleIconProps extends React.HTMLAttributes<HTMLAnchorElement> {}

export const Facebook: React.FC<SimpleIconProps> = (props) => (
  <SocialIconBase
    href="https://www.facebook.com/LeicesterCityChurch/"
    Icon={FacebookIcon}
    aria-label="Find us on Facebook"
    {...props} // Pass through all props (including className) to the base
  />
);

export const Instagram: React.FC<SimpleIconProps> = (props) => (
  <SocialIconBase
    href="https://www.instagram.com/leicesterpiwc/"
    Icon={InstagramLogo}
    aria-label="Follow us on Instagram"
    {...props}
  />
);

export const Youtube: React.FC<SimpleIconProps> = (props) => (
  <SocialIconBase
    href="https://www.youtube.com/@leicesterpiwc"
    Icon={YoutubeLogo}
    aria-label="Watch our videos on YouTube"
    {...props}
  />
);
